import {
  convertSchemaToHtml,
  type Options,
} from '@thebeyondgroup/shopify-rich-text-renderer';

import { portableTextToShopifyRichText } from './shopify-rich-text';
import type { ContentImage, ContentMedia, RichText } from './types';

type RichTextHtmlOptions = Pick<Options, 'newLineToBreak' | 'classes'>;

// null for empty input, since the renderer throws on an empty root.
export function richTextToHtml(
  value?: RichText,
  options?: RichTextHtmlOptions,
): string | null {
  if (!value?.length) return null;
  return convertSchemaToHtml(portableTextToShopifyRichText(value), options);
}

export const hasRichText = (value: unknown): value is RichText =>
  Array.isArray(value) && value.length > 0;

// The image in a slot that also takes video; a video there reads as no image.
export const imageOf = (media?: ContentMedia): ContentImage | undefined =>
  media?.kind === 'image' ? media : undefined;

// A size variant, falling back to the full-size URL.
export const imageUrl = (
  image: ContentImage | undefined,
  size: 'large' | 'medium' | 'thumbnail',
): string | undefined => image?.[`${size}Url` as const] || image?.url;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// Deep: drops null/undefined object values and array members; keeps [], {}, '' and false.
export function stripNulls<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.filter((item) => item != null).map(stripNulls) as T;
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, stripNulls(v)]),
    ) as T;
  }
  return value;
}
